import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, List, Typography } from '@mui/material';
import { PropertyListItem } from '@atoms';
import { useNamePartnerCustomers } from '@hooks';
import { BoxContainer, FullWidthContent, MainMenuLayout } from '@layouts';
import { GoBackButton } from '@molecules';
import { DETAIL_ITEMS } from './constants';
import useGetPendingDispersionById from '../data/useGetPendingDispersionById';

/**
 * Detail screen for a pending dispersion creation
 */
const PendingDispersionDetailScreen: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { getNamePartnerCustomer } = useNamePartnerCustomers();
  const { dispersion: response, isLoading } = useGetPendingDispersionById(id);

  const dispersion = useMemo(() => {
    if (!response) return undefined;
    return {
      ...response,
      companyName:
        response.customerName || getNamePartnerCustomer(String(response.customerId)) || undefined,
    };
  }, [getNamePartnerCustomer, response]);

  /**
   * Navigates back to the pending dispersions list
   */
  const goBack = () => {
    navigate('/dispersiones/gestion-pendientes');
  };

  return (
    <MainMenuLayout>
      <FullWidthContent>
        <BoxContainer>
          <Typography variant='h4' mb={3}>
            {t('PENDING_DISPERSIONS:DETAIL:TITLE')}
          </Typography>
          {isLoading ? (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={48} />
            </Box>
          ) : dispersion ? (
            <List>
              {DETAIL_ITEMS.map(({ label, getText }) => {
                const value = getText(dispersion);
                return (
                  <PropertyListItem
                    key={label}
                    label={label}
                    value={
                      value || (
                        <Typography
                          sx={{ fontStyle: 'italic', fontSize: 15, color: 'neutral.400' }}
                        >
                          {t('COMMON:NO_INFORMATION')}
                        </Typography>
                      )
                    }
                  />
                );
              })}
            </List>
          ) : (
            <Alert variant='filled' severity='error'>
              {t('PENDING_DISPERSIONS:DETAIL:MISSING')}
            </Alert>
          )}
        </BoxContainer>
        <GoBackButton
          variant='outlined'
          text='COMMON:GO_BACK'
          onClick={() => {}}
          backFunction={goBack}
        />
      </FullWidthContent>
    </MainMenuLayout>
  );
};

export default PendingDispersionDetailScreen;
